import React, { useEffect, useMemo, useState } from "react";
import { useAdminUserListQuery } from "generated/graphql";
import { Badge, Button, Container, Group, Paper, Pagination, Table, Text, TextInput, Title, useMantineColorScheme } from "@mantine/core";
import { IconArrowLeft, IconSearch } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { UserFlag, UserFlagBadge } from "../../components/Badges";

export default function UserManagement() {
  const navigate = useNavigate();
  return (
    <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
      <Group justify="flex-start" mb="sm">
        <Button
          variant="filled"
          color="blue"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate('/admin-hub')}
          size="sm"
        >
          Back to Admin Hub
        </Button>
      </Group>
      <UserTable />
    </Container>
  );
}

const pageSize = 10;

function UserTable() {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const { data, loading, error, refetch } = useAdminUserListQuery({
    variables: {
      skip: 0,
      take: pageSize,
    },
    fetchPolicy: "cache-and-network",
  });

  const [userData, setUserData] = useState<UserTableRowData[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [query, setQuery] = useState("");

  useEffect(() => {
    setUserData(
      data?.users?.items?.map((ele) => {
        const roles = (ele?.roles ?? []).filter((r): r is string => !!r);
        // Get flags from actual API data
        const flags: UserFlag[] = [];
        if (ele?.isBanned) flags.push("Banned");
        if (ele?.isSuspended) flags.push("Suspended");
        if (ele?.isWatchlist) flags.push("Watchlist");
        return {
          id: ele?.id ?? "",
          name: ele?.name ?? "",
          roles,
          flags,
        };
      }) ?? []
    );

    if (data?.users?.totalCount) {
      setPageCount(Math.ceil(data.users.totalCount / pageSize));
    }
  }, [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return userData;
    return userData.filter((u) => [u.name, u.id, ...(u.roles ?? []), ...(u.flags ?? [])].some((v) => v.toLowerCase().includes(q)));
  }, [query, userData]);

  function onSelectUser(id: string) {
    navigate(`/admin-hub/users/${id}`);
  }

  return (
    <div className="space-y-4">
      <Title c={isDark ? "white" : "black"} size="h3" className="sm:text-2xl">User Management</Title>
      <Text c={isDark ? "gray.4" : "gray.7"} size="sm">
        Manage users, view their roles, and set administrative flags.
      </Text>

      <TextInput
        placeholder="Search users by name, id, or role"
        leftSection={<IconSearch size={16} />}
        value={query}
        onChange={(e) => setQuery(e.currentTarget.value)}
        className="w-full sm:max-w-xs"
      />

      <Paper withBorder radius="md" shadow="sm" className="overflow-x-auto">
        <Table
          striped
          highlightOnHover
          withTableBorder
          withColumnBorders
          horizontalSpacing="md"
          verticalSpacing="sm"
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>User</Table.Th>
              <Table.Th>User ID</Table.Th>
              <Table.Th>Roles</Table.Th>
              <Table.Th>Flags</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.map((u) => (
              <Table.Tr
                key={u.id}
                onClick={() => onSelectUser(u.id)}
                role="button"
                tabIndex={0}
                aria-label={`Open user ${u.name}`}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") onSelectUser(u.id);
                }}
                style={{ cursor: "pointer" }}
              >
                <Table.Td>{u.name || "-"}</Table.Td>
                <Table.Td>{u.id}</Table.Td>
                <Table.Td>
                  <Group gap={6} wrap="wrap">
                    {(u.roles ?? []).length > 0 ? (
                      (u.roles ?? []).map((r) => (
                        <Badge key={r} variant="dark">
                          {r}
                        </Badge>
                      ))
                    ) : (
                      <Text size="sm" c={isDark ? "gray.5" : "gray.6"}>
                        No roles
                      </Text>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                    <UserFlagsCell flags={u.flags} isDark={isDark} />
                </Table.Td>
              </Table.Tr>
            ))}
            {filtered.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text size="sm" c={isDark ? "gray.5" : "gray.6"}>
                    {loading ? "Loading users..." : error ? "Failed to load users" : "No users match your search."}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <Text size="sm" c={isDark ? "gray.5" : "gray.6"} className="text-center sm:text-left">
          Showing {filtered.length} user{filtered.length === 1 ? "" : "s"} on this page
        </Text>
        <Pagination
          total={pageCount}
          value={activePage}
          onChange={(i) => {
            setActivePage(i);
            refetch({
              skip: (i - 1) * pageSize,
              take: pageSize,
            });
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          size="sm"
          className="justify-center sm:justify-end"
        />
      </div>
    </div>
  );
}

function UserFlagsCell({flags, isDark}:{flags: UserFlag[], isDark: boolean}){

    if(flags === undefined || flags === null || flags.length <= 0){
        return <Text size="sm" c={isDark ? "gray.5" : "gray.6"}>
            N/A
        </Text>
    }
    return <Group gap={6} wrap="wrap">
        {flags.map((f) => <UserFlagBadge key={f} type={f} />)}
    </Group>
}

interface UserTableRowData {
  id: string;
  name: string;
  roles: string[];
  flags: UserFlag[];
}
